import { useEffect, useState, type DragEvent } from 'react';
import { Link } from 'react-router';
import type { LocalDate } from '@habitos/engine';
import { useI18n } from '../i18n';
import { buildTimeline, type AgendaEntry } from '../domain/agenda';
import type { HabitRec } from '../domain/model';
import { DRAG_TYPE, useMoveTask } from './AgendaView';

/** Alto de una hora en la línea de tiempo (px). */
const HOUR_PX = 56;
const SNAP_MIN = 15;

const hhmm = (min: number) => `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;

/** Minutos de ahora en la zona del usuario, actualizados cada minuto. */
function useNowMinutes(tz: string): number {
  const read = () => {
    const [h, m] = new Intl.DateTimeFormat('en-GB', { timeZone: tz, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(new Date()).split(':');
    return Number(h) * 60 + Number(m);
  };
  const [now, setNow] = useState(read);
  useEffect(() => {
    const id = window.setInterval(() => setNow(read()), 60_000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tz]);
  return now;
}

/**
 * Día como línea de tiempo: cada actividad con hora ocupa su duración real (o 30 min
 * estimados, marcados), los solapes se reparten en columnas y, si es hoy, una línea marca la
 * hora actual. Las tareas se pueden arrastrar a otra hora (ratón) o tocar para reprogramarlas.
 */
export function DayTimeline({ entries, day, today, tz, onMove }: { entries: AgendaEntry[]; day: LocalDate; today: LocalDate; tz: string; onMove: (h: HabitRec) => void }) {
  const { t } = useI18n();
  const move = useMoveTask();
  const now = useNowMinutes(tz);
  const [hover, setHover] = useState<number | null>(null);
  const tl = buildTimeline(entries);
  const hours = Array.from({ length: tl.toHour - tl.fromHour }, (_, i) => tl.fromHour + i);
  const top = (min: number) => ((min - tl.fromHour * 60) / 60) * HOUR_PX;
  const minutesAt = (e: DragEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const raw = tl.fromHour * 60 + ((e.clientY - rect.top) / HOUR_PX) * 60;
    return Math.max(0, Math.min(24 * 60 - SNAP_MIN, Math.round(raw / SNAP_MIN) * SNAP_MIN));
  };

  return (
    <div className="stack">
      {tl.untimed.length > 0 && (
        <section aria-labelledby="tl-untimed" className="stack" style={{ gap: 'var(--space-1)' }}>
          <h2 id="tl-untimed" className="section-title">
            {t('agenda.slots.anytime')}
          </h2>
          <ul className="week__list">
            {tl.untimed.map((e) => (
              <li key={e.key} className={`week__item week__item--${e.kind}${e.done ? ' week__item--done' : ''}`}>
                <span className="week__title">{e.title}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
      <div
        className="timeline"
        style={{ height: hours.length * HOUR_PX }}
        role="region"
        aria-label={t('agenda.timeline')}
        onDragOver={(e) => {
          if (!e.dataTransfer.types.includes(DRAG_TYPE) || day < today) return;
          e.preventDefault();
          setHover(minutesAt(e));
        }}
        onDragLeave={() => setHover(null)}
        onDrop={(e) => {
          e.preventDefault();
          const id = e.dataTransfer.getData(DRAG_TYPE);
          const target = tl.blocks.find((b) => b.entry.habit?.id === id)?.entry.habit;
          const min = minutesAt(e);
          setHover(null);
          if (target && day >= today) void move(target, day, hhmm(min));
        }}
      >
        {hours.map((h) => (
          <div key={h} className="timeline__hour" style={{ top: (h - tl.fromHour) * HOUR_PX }} aria-hidden="true">
            <span className="num">{hhmm(h * 60)}</span>
          </div>
        ))}
        {day === today && now >= tl.fromHour * 60 && now <= tl.toHour * 60 && (
          <div className="timeline__now" style={{ top: top(now) }} aria-label={t('agenda.nowAt', { time: hhmm(now) })} role="img" />
        )}
        {hover !== null && <div className="timeline__drop num" style={{ top: top(hover) }}>{hhmm(hover)}</div>}
        {tl.blocks.map((b) => {
          const style = { top: top(b.start), height: Math.max(22, top(b.end) - top(b.start) - 2), left: `calc(52px + (100% - 56px) * ${b.col / b.cols})`, width: `calc((100% - 56px) / ${b.cols} - 4px)` };
          const label = `${b.entry.time}–${b.entry.end}${b.entry.estimated ? '*' : ''}`;
          const cls = `timeline__block timeline__block--${b.entry.kind}${b.entry.done ? ' timeline__block--done' : ''}`;
          const body = (
            <>
              <span className="timeline__title">{b.entry.title}</span>
              <span className="timeline__time num">{label}</span>
            </>
          );
          if (b.entry.kind === 'task' && b.entry.habit) {
            const h = b.entry.habit;
            const movable = !b.entry.done && day >= today;
            return (
              <button
                key={b.entry.key}
                type="button"
                className={cls}
                style={style}
                draggable={movable}
                onDragStart={(e) => {
                  e.dataTransfer.setData(DRAG_TYPE, h.id);
                  e.dataTransfer.effectAllowed = 'move';
                }}
                onClick={() => movable && onMove(h)}
                aria-label={`${b.entry.title}, ${label}. ${movable ? t('agenda.moveOther') : t('habit.status.completed')}`}
              >
                {body}
              </button>
            );
          }
          return (
            <Link key={b.entry.key} to={`/habits/${b.entry.habit!.id}`} className={cls} style={style}>
              {body}
            </Link>
          );
        })}
      </div>
      {tl.blocks.some((b) => b.entry.estimated) && <p className="muted xsmall">{t('agenda.estimatedLegend')}</p>}
    </div>
  );
}
